import React, { ReactElement, useEffect, useCallback } from 'react'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import { useWebSettingsState } from 'live/settings/useWebSettingsState'
import globalNotification from 'live/services/global-notification'
import FormGroup from 'live/components/forms/ui/form-group'
import ActionToggle from 'live/components/ui/action-toggle'
import i18n from 'live/services/i18n'
import getCustomSendSmtpDelay from '../js/api/getSendSmtpDelay'
import setCustomSendSmtpDelay from '../js/api/setSendSmtpDelay'
import { ResponseType } from '../js/common/types/api/request'
import getOneEmailForRecipientGroupFlag from '../js/api/getOneEmailForRecipientGroupFlag'
import setOneEmailForRecipientGroupFlag from '../js/api/setOneEmailForRecipientGroupFlag'
import getKeepScreenshotAfterProcessFlag from '../js/api/getKeepScreenshotAfterProcessFlag'
import setKeepScreenshotAfterProcessFlag from '../js/api/setKeepScreenshotAfterProcessFlag'
import getKeepRequestJsonAfterProcessFlag from '../js/api/getReepRequestJsonAfterProcessFlag'
import setKeepRequestJsonAfterProcessFlag from '../js/api/setKeepRequestJsonAfterProcessFlag'
import './registerAdminConfig'
import { getPuppeteerMainPage, getPuppeteerHost, setPuppeteerHost, setPuppeteerMainPage } from '../js/api/apiConfigHost'
interface basicReportsMakerSettings {
    customSendSmtpDelay: number
    oneEmailForRecipientGroupFlag: boolean
    keepScreenshotAfterProcessFlag: boolean
    keepRequestJsonAfterProcessFlag: boolean
    puppeteerHost: string
    puppeteerMainPage: string
}

interface ReportsMakerSettings extends basicReportsMakerSettings {
    originalValues?: basicReportsMakerSettings
}

function ReportsMakerSettingsPage(): ReactElement {
    const [reportsMakerSettings, setReportsMakerSettings] = React.useState<ReportsMakerSettings>({
        customSendSmtpDelay: -1,
        oneEmailForRecipientGroupFlag: false,
        keepScreenshotAfterProcessFlag: false,
        keepRequestJsonAfterProcessFlag: false,
        puppeteerHost: '',
        puppeteerMainPage: ''
    })

    const { state, actions } = useWebSettingsState(reportsMakerSettings)

    const fetchItems = useCallback(() => {
        Promise.all([
            getCustomSendSmtpDelay(),
            getOneEmailForRecipientGroupFlag(),
            getKeepScreenshotAfterProcessFlag(),
            getKeepRequestJsonAfterProcessFlag(),
            getPuppeteerHost(),
            getPuppeteerMainPage()
        ])
            .then(
                ([
                    currentCustomDelay,
                    oneEmailForRecipientGroupFlag,
                    keepScreenshotAfterProcessFlag,
                    keepRequestJsonAfterProcessFlag,
                    puppeteerHost,
                    puppeteerMainPage
                ]) => {
                    const config = {
                        customSendSmtpDelay: currentCustomDelay,
                        oneEmailForRecipientGroupFlag,
                        keepScreenshotAfterProcessFlag,
                        keepRequestJsonAfterProcessFlag,
                        puppeteerHost,
                        puppeteerMainPage
                    }
                    console.log('puppeteerHost', puppeteerMainPage)
                    return {
                        ...config,
                        originalValues: config
                    }
                }
            )
            .then(config => {
                setReportsMakerSettings(config)
                actions.setPersistedData(config)
            })
            .catch(err => {
                globalNotification.error(`${i18n('Failure loading reports-maker settings')}. ${err.message}`)
            })
    }, [actions])

    useEffect(fetchItems, [])

    const onSave = React.useCallback(() => {
        actions.submit()
        persist(reportsMakerSettings)
            .then(responses => {
                if (responses.length === 1) return responses[0]

                const failResponses = responses.filter(response => !response.success)
                if (failResponses.length > 0) {
                    console.error(failResponses.map(response => response.message))
                    return {
                        success: false,
                        message: i18n('check the console for more information'),
                        error: i18n('check the console for more information')
                    } as ResponseType
                }
                return {
                    success: true
                } as ResponseType
            })
            .then(response => {
                if (response.success) {
                    reportsMakerSettings.originalValues = {
                        customSendSmtpDelay: reportsMakerSettings.customSendSmtpDelay,
                        oneEmailForRecipientGroupFlag: reportsMakerSettings.oneEmailForRecipientGroupFlag,
                        keepScreenshotAfterProcessFlag: reportsMakerSettings.keepScreenshotAfterProcessFlag,
                        keepRequestJsonAfterProcessFlag: reportsMakerSettings.keepRequestJsonAfterProcessFlag,
                        puppeteerHost: reportsMakerSettings.puppeteerHost,
                        puppeteerMainPage: reportsMakerSettings.puppeteerMainPage
                    }
                    actions.saveSuccess(reportsMakerSettings)
                    globalNotification.success(i18n('Settings successfully saved'))
                } else {
                    const msg = `${i18n('Failure saving settings')}. ${response?.message}`
                    actions.setError(msg)
                    globalNotification.error(msg)
                }
            })
            .catch(async r => {
                actions.setError(await r.text())
            })
    }, [actions, reportsMakerSettings])

    return (
        <WebSettingsCard
            name="reports-maker-settings"
            className="live-feature mb-15"
            open={state.open}
            error={state.submitted ? state.error : null}
            saved={state.open && state.saved}
            save={onSave}
            toggle={actions.toggle}
            onCancel={() => setReportsMakerSettings(state.persistedData)}
        >
            <div className="live-feature__header">
                <h2 className="flex-1">{i18n('Current custom SMTP send delay')}</h2>
            </div>
            <FormGroup
                help={i18n(
                    'This is the interval between each email, that will be sent from reports-maker to the SMTP integration. The default value is 1800ms'
                )}
                value={reportsMakerSettings.customSendSmtpDelay}
                hideInput={!state.open}
                onChange={({ target }) => setReportsMakerSettings(s => ({ ...s, customSendSmtpDelay: target.value }))}
            />
            <div className="live-feature__header">
                <h2 className="flex-1">{i18n('LIVE HOST')}</h2>
            </div>
            <FormGroup
                help={i18n('')}
                value={reportsMakerSettings.puppeteerHost}
                hideInput={!state.open}
                onChange={({ target }) => setReportsMakerSettings(s => ({ ...s, puppeteerHost: target.value }))}
            />
            <div className="live-feature__header">
                <h2 className="flex-1">{i18n('PUPPETEER: INITIAL PAGE')}</h2>
            </div>
            <FormGroup
                help={i18n('')}
                value={reportsMakerSettings.puppeteerMainPage}
                hideInput={!state.open}
                onChange={({ target }) => setReportsMakerSettings(s => ({ ...s, puppeteerMainPage: target.value }))}
            />{' '}
            <div style={{ marginBottom: '25px' }}>
                <div style={{ marginBottom: '10px' }}>
                    <ActionToggle
                        name="web-settings-reportsmaker-disableOneEmailForRecipientGroups"
                        onChange={() =>
                            setReportsMakerSettings(s => ({
                                ...s,
                                oneEmailForRecipientGroupFlag: !reportsMakerSettings.oneEmailForRecipientGroupFlag
                            }))
                        }
                        elLeft={<span>on</span>}
                        elRight={<span>off</span>}
                        active={reportsMakerSettings.oneEmailForRecipientGroupFlag}
                        disabled={!state.open}
                    />
                    <span>{i18n('Send all Recipient Group recipients in the same email as BCC.')}</span>
                </div>
                <div style={{ marginBottom: '10px' }}>
                    <ActionToggle
                        name="web-settings-reportsmaker-disableKeepScreenshotAfterProcessFlag"
                        onChange={() =>
                            setReportsMakerSettings(s => ({
                                ...s,
                                keepScreenshotAfterProcessFlag: !reportsMakerSettings.keepScreenshotAfterProcessFlag
                            }))
                        }
                        elLeft={<span>on</span>}
                        elRight={<span>off</span>}
                        active={reportsMakerSettings.keepScreenshotAfterProcessFlag}
                        disabled={!state.open}
                    />
                    <span>{i18n("Keep at the temp folder the SCREENSHOTS cache taken at the report's process.")}</span>
                </div>
                <div>
                    <ActionToggle
                        name="web-settings-reportsmaker-disableKeepRequestJsonAfterProcessFlag"
                        onChange={() =>
                            setReportsMakerSettings(s => ({
                                ...s,
                                keepRequestJsonAfterProcessFlag: !reportsMakerSettings.keepRequestJsonAfterProcessFlag
                            }))
                        }
                        elLeft={<span>on</span>}
                        elRight={<span>off</span>}
                        active={reportsMakerSettings.keepRequestJsonAfterProcessFlag}
                        disabled={!state.open}
                    />
                    <span>
                        {i18n(
                            "Keep at the temp folder the JSON REQUEST for the screenshots cache taken at the report's process."
                        )}
                    </span>
                </div>
            </div>
        </WebSettingsCard>
    )
}

const persist = (config: ReportsMakerSettings): Promise<ResponseType[]> => {
    const promises = [] as Promise<ResponseType>[]
    if (config.customSendSmtpDelay !== config.originalValues?.customSendSmtpDelay)
        promises.push(setCustomSendSmtpDelay(config.customSendSmtpDelay))
    if (config.oneEmailForRecipientGroupFlag !== config.originalValues?.oneEmailForRecipientGroupFlag)
        promises.push(setOneEmailForRecipientGroupFlag(config.oneEmailForRecipientGroupFlag))
    if (config.keepScreenshotAfterProcessFlag !== config.originalValues?.keepScreenshotAfterProcessFlag)
        promises.push(setKeepScreenshotAfterProcessFlag(config.keepScreenshotAfterProcessFlag))
    if (config.keepRequestJsonAfterProcessFlag !== config.originalValues?.keepRequestJsonAfterProcessFlag)
        promises.push(setKeepRequestJsonAfterProcessFlag(config.keepRequestJsonAfterProcessFlag))

    if (config.puppeteerHost !== config.originalValues?.puppeteerHost && config.puppeteerHost !== '')
        promises.push(setPuppeteerHost(config.puppeteerHost))
    
    if (config.puppeteerMainPage !== config.originalValues?.puppeteerMainPage && config.puppeteerMainPage !== '')
        promises.push(setPuppeteerMainPage(config.puppeteerMainPage))

    return Promise.all(promises)
}

export default {
    persist: persist,
    configComponent: ReportsMakerSettingsPage,
    section: i18n('Reports maker'),
    name: i18n('Reports maker settings')
}
